import React, { memo, useMemo } from "react";
import { ResourceEntity } from "../services/types/resourceEntity";
import HStack from "@/pages/taskpane/components/layout/HStack";
import Tooltip from "@/pages/taskpane/components/ui/Tooltip";
import { useAppConfigStore, useOutlookStore, useResourceStore, useThemeStore } from "@/pages/taskpane/shared/stores";
import { ClosingTimeTypeEnum } from "@/pages/taskpane/shared/types/enums/globalEnum";
import { EPermissionType } from "../services/types/enum";
import { PERMISSION_DESCRIPTION } from "@/pages/taskpane/shared/constants/permission";
import { useTranslation } from "react-i18next";
import {
  PersonAvailableIcon,
  ProhibitedIcon,
  WarningBlueIcon,
  WarningGreenIcon,
  WarningRedIcon,
  WarningYellowIcon,
} from "@/pages/taskpane/components/icons/IconCustom";
import { handleFormatIntToDayOfWeek } from "@/pages/taskpane/shared/helpers/handleFormatIntToDayOfWeek";
import { useFormatDateTime } from "@/pages/taskpane/shared/hooks/useFormatDateTime";

type Props = {
  resource: ResourceEntity;
  isNoTooltip?: boolean;
  isVertical?: boolean;
  fontSizeClassname?: string;
};

const IconPermission = ({
  resource,
  isNoTooltip = false,
  isVertical = false,
  fontSizeClassname = "text-xs",
}: Props) => {
  const { t } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const { parameters } = useAppConfigStore();
  const { recurringDates } = useOutlookStore();
  const { selectedResource, deskLocationList } = useResourceStore();
  const { handleFormatDateWithSeparator } = useFormatDateTime();
  const isRecurring = useMemo(() => recurringDates.length > 0, [recurringDates]);
  const availability = useMemo(() => resource.availability, [resource]);

  const AlertInfoIcon = useMemo(() => {
    const permissions = availability?.permissions(isRecurring);
    if (permissions && permissions.length > 0) {
      if (permissions.includes(EPermissionType.OccurrencceExceedCapacity)) {
        return {
          isShow: true,
          content: (
            <BookingPolicyContent
              fontSizeClassname={fontSizeClassname}
              content={t(PERMISSION_DESCRIPTION[EPermissionType.OccurrencceExceedCapacity])}
            />
          ),
        };
      }
      if (permissions.includes(EPermissionType.OccurenciesWithConflicts)) {
        return {
          isShow: true,
          content: (
            <BookingPolicyContent
              fontSizeClassname={fontSizeClassname}
              content={t(PERMISSION_DESCRIPTION[EPermissionType.OccurenciesWithConflicts])}
            />
          ),
        };
      }
      if (permissions.includes(EPermissionType.CanBookByExchangeByRule)) {
        return {
          isShow: true,
          content: (
            <BookingPolicyContent
              fontSizeClassname={fontSizeClassname}
              content={t(PERMISSION_DESCRIPTION[EPermissionType.CanBookByExchangeByRule])}
              className="mb-1"
            />
          ),
        };
      }
    }
    return {
      isShow: false,
      content: null,
    };
  }, [availability, isRecurring]);

  const UserVerifiedIcon = useMemo(() => {
    const permissions = availability?.permissions(isRecurring);
    if (permissions && permissions.length > 0) {
      if (permissions.includes(EPermissionType.DelegateByInOutPolicy)) {
        return {
          isShow: true,
          content: (
            <BookingPolicyContent
              fontSizeClassname={fontSizeClassname}
              content={t(PERMISSION_DESCRIPTION[EPermissionType.DelegateByInOutPolicy])}
            />
          ),
        };
      }
    }
    return {
      isShow: false,
      content: null,
    };
  }, [availability]);

  const AccessDeniedIcon = useMemo(() => {
    const permissions = availability?.permissions(isRecurring);
    if (permissions && permissions.length > 0) {
      if (permissions.includes(EPermissionType.VRConfigWrong)) {
        return {
          isShow: true,
          content: (
            <BookingPolicyContent
              fontSizeClassname={fontSizeClassname}
              content={t(PERMISSION_DESCRIPTION[EPermissionType.VRConfigWrong])}
            />
          ),
        };
      }
      if (permissions.includes(EPermissionType.DeclinedByInPolicy)) {
        return {
          isShow: true,
          content: (
            <BookingPolicyContent
              fontSizeClassname={fontSizeClassname}
              content={t(PERMISSION_DESCRIPTION[EPermissionType.DeclinedByInPolicy])}
            />
          ),
        };
      }
      if (availability?.closingTimeType === ClosingTimeTypeEnum.CloseDate) {
        return {
          isShow: true,
          content: (
            <div className={fontSizeClassname}>
              <p className="font-bold">{t("Closed")}:</p>
              <p>
                {t("Opening Date")}: {resource?.openingDate ? handleFormatDateWithSeparator(resource.openingDate) : t("None")}
              </p>
            </div>
          ),
        };
      }
    }
    return {
      isShow: false,
      content: null,
    };
  }, [availability, handleFormatDateWithSeparator, resource?.openingDate]);

  const AlertDangerIcon = useMemo(() => {
    const permissions = availability?.permissions(isRecurring);
    if (permissions && permissions.length > 0) {
      if (permissions.includes(EPermissionType.AllOccurrenceExceedCapacity)) {
        return {
          isShow: true,
          content: (
            <div>
              <BookingPolicyContent
                fontSizeClassname={fontSizeClassname}
                content={t(PERMISSION_DESCRIPTION[EPermissionType.AllOccurrenceExceedCapacity])}
                className="mb-1"
              />
              <ReasonContent
                fontSizeClassname={fontSizeClassname}
                content={t(PERMISSION_DESCRIPTION[EPermissionType.ExceedCapacity])}
              />
            </div>
          ),
        };
      }
      if (permissions.includes(EPermissionType.NotAllowRecurringBooking)) {
        return {
          isShow: true,
          content: (
            <BookingPolicyContent
              fontSizeClassname={fontSizeClassname}
              content={t(PERMISSION_DESCRIPTION[EPermissionType.NotAllowRecurringBooking])}
            />
          ),
        };
      }
      if (permissions.includes(EPermissionType.DeclinedByOutPolicy)) {
        return {
          isShow: true,
          content: (
            <BookingPolicyContent
              fontSizeClassname={fontSizeClassname}
              content={t(PERMISSION_DESCRIPTION[EPermissionType.DeclinedByOutPolicy])}
            />
          ),
        };
      }
      if (permissions.includes(EPermissionType.ExceedCapacity)) {
        return {
          isShow: true,
          content: (
            <div>
              <BookingPolicyContent
                fontSizeClassname={fontSizeClassname}
                content={t(PERMISSION_DESCRIPTION[EPermissionType.ExceedCapacity])}
                className="mb-1"
              />
              <ReasonContent
                fontSizeClassname={fontSizeClassname}
                content={t("The resource is not available at the requested time.")}
              />
            </div>
          ),
        };
      }
      if (permissions.includes(EPermissionType.CannotBookByExchangeByRule)) {
        return {
          isShow: true,
          content: (
            <BookingPolicyContent
              fontSizeClassname={fontSizeClassname}
              content={t(PERMISSION_DESCRIPTION[EPermissionType.CannotBookByExchangeByRule])}
            />
          ),
        };
      }
      if (permissions.includes(EPermissionType.ViolatePriority)) {
        return {
          isShow: true,
          content: (
            <BookingPolicyContent
              fontSizeClassname={fontSizeClassname}
              content={t(PERMISSION_DESCRIPTION[EPermissionType.ViolatePriority], {
                daysReserved: parameters.daysReserved,
              })}
            />
          ),
        };
      }
    }

    if (
      resource?.isDisableResourceByHotDeskRule(
        selectedResource.filter((r) => r.id !== resource.id),
        deskLocationList
      )
    ) {
      return {
        isShow: true,
        content: (
          <div>
            <BookingPolicyContent
              fontSizeClassname={fontSizeClassname}
              content={t(PERMISSION_DESCRIPTION[EPermissionType.HotDeskResource])}
            />
            <ReasonContent
              fontSizeClassname={fontSizeClassname}
              content={t(PERMISSION_DESCRIPTION[EPermissionType.ExceedCapacity])}
            />
          </div>
        ),
      };
    }
    return {
      isShow: false,
      content: null,
    };
  }, [availability, isRecurring, deskLocationList, selectedResource]);

  const AlertWarningIcon = useMemo(() => {
    const permissions = availability?.permissions(isRecurring);
    const tooltip: React.ReactNode[] = [];
    if (permissions && permissions.length > 0) {
      if (permissions.includes(EPermissionType.PreCloseDate)) {
        tooltip.push(
          <div className={fontSizeClassname}>
            <p className="font-bold">{t("Closed Period")}:</p>
            <p>
              {t("Closing Date")}: {resource?.closingDate ? handleFormatDateWithSeparator(resource.closingDate) : t("None")}
            </p>
            <p>
              {t("Opening Date")}: {resource?.openingDate ? handleFormatDateWithSeparator(resource.openingDate) : t("None")}
            </p>
          </div>
        );
      }
      if (permissions.includes(EPermissionType.PrioritizedReservation)) {
        tooltip.push(
          <div className={fontSizeClassname}>
            <p className="font-bold">{t("Prioritized Reservation")}:</p>
            <p>
              {t(PERMISSION_DESCRIPTION[EPermissionType.PrioritizedReservation], {
                days: availability.violatePriorityDayOfWeeks?.map(handleFormatIntToDayOfWeek)?.join(", "),
              })}
            </p>
          </div>
        );
      }
    }

    if (tooltip.length > 0) {
      return {
        isShow: true,
        content: (
          <>
            {tooltip.map((item, index) => (
              <React.Fragment key={`tooltip-${resource?.email}`}>
                {item}
                {index < tooltip.length - 1 && <div className="mt-2" />}
              </React.Fragment>
            ))}
          </>
        ),
      };
    } else {
      return {
        isShow: false,
        content: null,
      };
    }
  }, [availability, resource?.closingDate, resource?.openingDate, isRecurring, handleFormatDateWithSeparator]);

  const AlertSuccessIcon = useMemo(() => {
    return {
      isShow: false,
      content: null,
    };
  }, [availability]);

  const renderContent = () => {
    const arr = [
      UserVerifiedIcon.isShow ? (
        !isNoTooltip ? (
          <Tooltip message={UserVerifiedIcon.content} position="top">
            <PersonAvailableIcon isDarkMode={isDarkMode} />
          </Tooltip>
        ) : (
          <div className="flex gap-2 items-center">
            <div className="flex items-center flex-shrink-0">
              <PersonAvailableIcon isDarkMode={isDarkMode} />
            </div>
            <span className={`${fontSizeClassname} text-darkText dark:text-white break-words`}>
              {UserVerifiedIcon.content}
            </span>
          </div>
        )
      ) : null,

      AlertInfoIcon.isShow ? (
        !isNoTooltip ? (
          <Tooltip message={AlertInfoIcon.content} position="top">
            <WarningBlueIcon isDarkMode={isDarkMode} />
          </Tooltip>
        ) : (
          <div className="flex gap-2 items-center">
            <div className="flex items-center flex-shrink-0">
              <WarningBlueIcon isDarkMode={isDarkMode} />
            </div>
            <span className={`${fontSizeClassname} text-darkText dark:text-white break-words`}>
              {AlertInfoIcon.content}
            </span>
          </div>
        )
      ) : null,

      AccessDeniedIcon.isShow ? (
        !isNoTooltip ? (
          <Tooltip message={AccessDeniedIcon.content} position="top">
            <ProhibitedIcon isDarkMode={isDarkMode} />
          </Tooltip>
        ) : (
          <div className="flex gap-2 items-center">
            <div className="flex items-center flex-shrink-0">
              <ProhibitedIcon isDarkMode={isDarkMode} />
            </div>
            <span className={`${fontSizeClassname} text-darkText dark:text-white break-words`}>
              {AccessDeniedIcon.content}
            </span>
          </div>
        )
      ) : null,

      AlertDangerIcon.isShow && !AccessDeniedIcon.isShow ? (
        !isNoTooltip ? (
          <Tooltip message={AlertDangerIcon.content} position="top">
            <WarningRedIcon isDarkMode={isDarkMode} />
          </Tooltip>
        ) : (
          <div className="flex gap-2 items-center">
            <div className="flex items-center flex-shrink-0">
              <WarningRedIcon isDarkMode={isDarkMode} />
            </div>
            <span className={`${fontSizeClassname} text-darkText dark:text-white break-words`}>
              {AlertDangerIcon.content}
            </span>
          </div>
        )
      ) : null,

      AlertWarningIcon.isShow ? (
        !isNoTooltip ? (
          <Tooltip message={AlertWarningIcon.content} position="top">
            <WarningYellowIcon isDarkMode={isDarkMode} />
          </Tooltip>
        ) : (
          <div className="flex gap-2 items-center">
            <div className="flex items-center flex-shrink-0">
              <WarningYellowIcon isDarkMode={isDarkMode} />
            </div>
            <span className={`${fontSizeClassname} text-darkText dark:text-white break-words`}>
              {AlertWarningIcon.content}
            </span>
          </div>
        )
      ) : null,

      AlertSuccessIcon.isShow ? (
        !isNoTooltip ? (
          <Tooltip message={AlertSuccessIcon.content} position="top">
            <WarningGreenIcon isDarkMode={isDarkMode} />
          </Tooltip>
        ) : (
          <div className="flex gap-2 items-center">
            <div className="flex items-center flex-shrink-0">
              <WarningGreenIcon isDarkMode={isDarkMode} />
            </div>
            <span className={`${fontSizeClassname} text-darkText dark:text-white break-words`}>
              {AlertSuccessIcon.content}
            </span>
          </div>
        )
      ) : null,
    ];

    const arrFiltered = arr.filter((item) => !!item);

    return arrFiltered.length === 0 ? null : (
      <>
        {arrFiltered.map((item, index) => (
          <React.Fragment key={`icon-permission-${index}`}>{item}</React.Fragment>
        ))}
      </>
    );
  };

  const content = renderContent();

  if (!content) return null;

  return isVertical ? (
    <div className="flex flex-col gap-1">{content}</div>
  ) : (
    <HStack alignItems="center" gap={2}>
      {content}
    </HStack>
  );
};

export default memo(IconPermission);

const BookingPolicyContent = ({
  content,
  fontSizeClassname = "text-xs",
  ...props
}: { content: string; fontSizeClassname?: string } & React.HTMLAttributes<HTMLDivElement>) => {
  const { t } = useTranslation();
  return (
    <div className={fontSizeClassname} {...props}>
      <p className="font-bold">{t("Booking policy")}:</p>
      <p>{content}</p>
    </div>
  );
};

const ReasonContent = ({
  content,
  fontSizeClassname = "text-xs",
  ...props
}: { content: string; fontSizeClassname?: string } & React.HTMLAttributes<HTMLDivElement>) => {
  const { t } = useTranslation();
  return (
    <div className={fontSizeClassname} {...props}>
      <p className="font-bold">{t("Reason")}:</p>
      <p>{content}</p>
    </div>
  );
};
